<template>
  <PrimeAutoComplete
    ref="autoRef"
    v-model="modelValue"
    v-model:searchValue="searchValueModel"
    :suggestions="suggestions"
    :loading="loading"
    :disabled="disabled"
    :multiple="multiple"
    :forceSelection="forceSelection"
    :minLength="minLength"
    :delay="delay"
    :appendTo="appendTo"
    :optionLabel="optionLabel"
    :dataKey="dataKey"
    :virtualScrollerOptions="virtualScrollerOptions"
    :completeOnFocus="completeOnFocus"
    :scrollHeight="scrollHeight"
    :fluid="fluid"
    unstyled
    :pt="passThrough"
    v-on="listeners"
    v-bind="$attrs"
  >
    <!-- Option slot -->
    <template #option="scope">
      <slot name="option" v-bind="scope">
        <div
          class="text-body-base text-neutral-text-body p-s w-full hover:bg-neutral-surface-muted transition-all duration-200"
        >
          {{ getOptionLabel(scope.option) }}
        </div>
      </slot>
    </template>

    <!-- Chip slot -->
    <template #chip="scope">
      <slot name="chip" v-bind="scope">
        <MeridiaChip
          :label="getOptionLabel(scope.value)"
          :removable="!disabled"
          @removeClicked="(e) => scope.removeCallback?.(e)"
        />
      </slot>
    </template>

    <!-- Empty slot -->
    <template #empty>
      <slot name="empty">
        <div class="meridia-autocomplete-no-options">No options found</div>
      </slot>
    </template>
    <template
      v-for="name in Object.keys(slots).filter(
        (key) =>
          !['empty', 'chip', 'option'].includes(key)
      )"
      :key="name"
      v-slot:[name]="slotData"
    >
      <slot :name="name" v-bind="slotData || {}"></slot>
    </template>
  </PrimeAutoComplete>
</template>

<script setup lang="ts" generic="T">
import { computed, ref, watch } from 'vue';
import PrimeAutoComplete from 'primevue/autocomplete';
import { usePassThrough } from 'primevue/passthrough';
import {
  AutoCompleteEmitsOptions,
  AutoCompletePassThroughOptions,
  AutoCompleteSlots,
} from 'primevue';
import { VirtualScrollerProps } from 'primevue/virtualscroller';
import { PassThrough } from '@primevue/core';
import MeridiaChip from 'src/design_system/components/chip/MeridiaChip.vue';
import { useListeners } from 'src/design_system/components/utils';

defineOptions({
  name: 'MeridiaAutocomplete',
});

const modelValue = defineModel<T[] | undefined>({ required: true });

const props = withDefaults(
  defineProps<{
    suggestions: T[];
    loading?: boolean;
    disabled?: boolean;
    multiple?: boolean;
    forceSelection?: boolean;
    minLength?: number;
    delay?: number;
    appendTo?: string | HTMLElement;
    optionLabel?: string;
    dataKey?: string;
    virtualScrollerOptions?: VirtualScrollerProps;
    completeOnFocus?: boolean;
    scrollHeight?: string;
    searchValue?: string;
    pt?: AutoCompletePassThroughOptions;
    fluid?: boolean;
  }>(),
  {
    loading: false,
    disabled: false,
    multiple: true,
    forceSelection: false,
    minLength: 1,
    delay: 0,
    appendTo: 'body',
    optionLabel: undefined,
    dataKey: undefined,
    completeOnFocus: false,
    scrollHeight: '16rem',
    searchValue: '',
    fluid: true,
  }
);

const slots = defineSlots<AutoCompleteSlots>();
const emit = defineEmits({
  'update:searchValue': (value: string) => typeof value === 'string',
});

const autoRef = ref<InstanceType<typeof PrimeAutoComplete> | null>(null);

const passThrough = usePassThrough(
  props.pt,
  {
    root: { class: 'flex align-center' },
    overlay: {
      class:
        'meridia-autocomplete-dropdown overflow-hidden mt-[14px] w-[400px]',
    },
    virtualScroller: {
      root: {
        class:
          '!h-80 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-400 scrollbar-track-transparent',
      },
    },
    inputMultiple: {
      class:
        'flex flex-1 items-center flex-nowrap whitespace-nowrap overflow-hidden',
    },
    chipitem: {
      class: 'flex items-center flex-nowrap leading-none h-[22px] gap-3xs',
    },
    inputchip: {
      class: 'flex flex-1 items-center ml-2xs mr-xs min-w-[50px]',
    },
    input: {
      class: 'outline-none text-body-base bg-transparent w-full',
    },
    loader: {
      class: '!flex self-center',
    },
  } as AutoCompletePassThroughOptions,
  { mergeSections: true, mergeProps: true }
) as PassThrough<AutoCompletePassThroughOptions>;

const { listeners } = useListeners<AutoCompleteEmitsOptions>(
  emit,
  PrimeAutoComplete
);

const searchValueModel = computed<string>({
  get() {
    return props.searchValue ?? '';
  },
  set(v: string) {
    emit('update:searchValue', v ?? '');
  },
});

watch(
  () => props.suggestions,
  () => {
    // Force new reference so PrimeVue reacts to updates
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    props.suggestions && [...props.suggestions];
  },
  { deep: true }
);

function getOptionLabel(option: any): string {
  if (option == null) return '';
  if (typeof option === 'string') return option;
  if (props.optionLabel && typeof option === 'object') {
    return option[props.optionLabel] ?? '';
  }
  return String(option);
}

defineExpose({
  focus: () => {
    const root = ((autoRef.value as any)?.$el ?? null) as HTMLElement | null;
    const input = root?.querySelector('input') as HTMLInputElement | null;
    if (input && document.activeElement !== input) {
      input.focus();
    }
  },
  hide: () => (autoRef.value as any)?.hide?.(),
  show: () => (autoRef.value as any)?.show?.(),
  autoRef,
});
</script>

<style
  src="src/design_system/components/autocomplete/autocomplete.scss"
></style>
